<template>
  <div
    v-if="totalCount > 0"
    class="paginator-container paginator-glass"
    :class="{ 'paginator-container--with-size': showItemsPerPage }"
  >
    <div class="paginator-info" :key="`paginator-info-${currentPage}-${recordsPerPage}`">
      <span class="paginator-info__text">
        Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
        ({{ rangeStart }}-{{ rangeEnd }} of {{ totalCount.toLocaleString() }})
      </span>
    </div>

    <div class="paginator-actions">
      <button
        type="button"
        class="paginator-btn paginator-btn-first"
        @click="goToPage(1)"
        :disabled="currentPage === 1 || loading"
        title="First page"
        aria-label="First page"
      >
        ««
      </button>
      <button
        type="button"
        class="paginator-btn paginator-btn-prev"
        @click="goToPage(currentPage - 1)"
        :disabled="currentPage === 1 || loading"
        title="Previous page"
        aria-label="Previous page"
      >
        ‹
      </button>

      <div class="paginator-jump">
        <input
          type="number"
          :min="1"
          :max="totalPages"
          v-model.number="jumpToPageInput"
          @keyup.enter="jumpToPage"
          class="paginator-jump-input"
          placeholder="Page"
          :disabled="loading"
          aria-label="Jump to page"
        />
        <button
          type="button"
          class="paginator-btn paginator-btn-jump"
          @click="jumpToPage"
          :disabled="loading || !jumpToPageInput || jumpToPageInput < 1 || jumpToPageInput > totalPages"
          title="Go to page"
        >
          Go
        </button>
      </div>

      <button
        type="button"
        class="paginator-btn paginator-btn-next"
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage >= totalPages || loading"
        title="Next page"
        aria-label="Next page"
      >
        ›
      </button>
      <button
        type="button"
        class="paginator-btn paginator-btn-last"
        @click="goToPage(totalPages)"
        :disabled="currentPage >= totalPages || loading"
        title="Last page"
        aria-label="Last page"
      >
        »»
      </button>
    </div>

    <div v-if="showItemsPerPage" class="paginator-size">
      <LabelDropdown
        label="Per page"
        inline
        :searchable="false"
        :select-id="itemsPerPageSelectId"
        v-model="itemsPerPageModel"
        :options="itemsPerPageOptions"
      />
    </div>
  </div>
</template>

<script>
import LabelDropdown from '../modules/consultant/components/LabelDropdown.vue';

export default {
  name: 'PaginatorComponent',
  components: { LabelDropdown },
  props: {
    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },
    totalCount: {
      type: Number,
      required: true,
      default: 0,
    },
    recordsPerPage: {
      type: Number,
      default: 100,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showItemsPerPage: {
      type: Boolean,
      default: false,
    },
    perPageOptions: {
      type: Array,
      default: null,
    },
    selectId: {
      type: String,
      default: 'paginator-items-per-page',
    },
  },
  emits: ['page-changed', 'items-per-page-change'],
  data() {
    return {
      jumpToPageInput: null,
    };
  },
  computed: {
    totalPages() {
      if (this.totalCount === 0 || this.recordsPerPage === 0) return 1;
      return Math.ceil(this.totalCount / this.recordsPerPage);
    },
    rangeStart() {
      return ((this.currentPage - 1) * this.recordsPerPage + 1).toLocaleString();
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.recordsPerPage, this.totalCount).toLocaleString();
    },
    itemsPerPageSelectId() {
      return this.selectId;
    },
    itemsPerPageOptions() {
      const sizes = this.perPageOptions || [10, 20, 50, 100, 200, 500];
      return sizes.map((size) => ({
        value: String(size),
        label: String(size),
      }));
    },
    itemsPerPageModel: {
      get() {
        return String(this.recordsPerPage);
      },
      set(value) {
        const parsed = parseInt(value, 10);
        if (Number.isFinite(parsed) && parsed > 0 && parsed !== this.recordsPerPage) {
          this.$emit('items-per-page-change', parsed);
        }
      },
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit('page-changed', page);
        this.jumpToPageInput = null;
      }
    },
    jumpToPage() {
      if (this.jumpToPageInput && this.jumpToPageInput >= 1 && this.jumpToPageInput <= this.totalPages) {
        this.goToPage(this.jumpToPageInput);
      }
    },
  },
  watch: {
    currentPage() {
      this.jumpToPageInput = null;
    },
    jumpToPageInput(newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== '') {
        const num = parseInt(newValue, 10);
        if (Number.isNaN(num) || num < 1) {
          this.jumpToPageInput = null;
        } else if (num > this.totalPages) {
          this.jumpToPageInput = this.totalPages;
        }
      }
    },
  },
};
</script>

<style src="./PaginatorComponent.css"></style>
